import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  TicketIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentListIcon,
  ArrowLeftStartOnRectangleIcon,
  ChevronRightIcon,
  UserPlusIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  DocumentTextIcon,
  SunIcon,
  MoonIcon,
  ChartBarIcon,
  SwatchIcon,
  BellIcon,
  FolderIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CommandLineIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

export default function Layout() {
  const { user, logout, organizations, notifications, unreadCount, handleMarkRead, handleMarkAllRead, pinnedOrgIds } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  // For admin sidebar: only show pinned orgs; for others: show all their orgs
  const sidebarOrgs = user?.role === 'admin'
    ? organizations.filter(o => pinnedOrgIds.includes(o._id))
    : organizations;

  // Convert event time in its timezone to user's local time for notification display
  const convertNotifTime = (date, time, tz) => {
    if (!date || !time || !tz) return time || '';
    try {
      const [y, mo, d] = date.split('-').map(Number);
      const [h, mi] = time.split(':').map(Number);
      const guess = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
      const inTz = new Date(guess.toLocaleString('en-US', { timeZone: tz }));
      const offsetMs = inTz.getTime() - guess.getTime();
      const utc = new Date(guess.getTime() - offsetMs);
      return utc.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return time;
    }
  };

  const buildNotifMessage = (n) => {
    if (!n.eventDate) return n.message;
    const localTime = n.eventTime ? convertNotifTime(n.eventDate, n.eventTime, n.eventTimezone) : null;
    // Extract event title from message (format: '"Title" on ...')
    const titleMatch = n.message.match(/^(".*?")/);
    const eventTitle = titleMatch ? titleMatch[1] : n.message.split('" on ')[0] + '"';
    return `${eventTitle} on ${n.eventDate}${localTime ? ' at ' + localTime : ''}`;
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleOrg = (orgId) => {
    setExpandedOrgs(prev => ({ ...prev, [orgId]: !prev[orgId] }));
  };

  const canManageBoards = ['owner', 'leader', 'admin'].includes(user?.role) || !!user?.permissions?.manageBoards;
  const canManageDiscord = (orgId) => {
    const org = organizations.find(o => o._id === orgId);
    return user?.role === 'admin' || org?.ownerId === user?._id || !!user?.permissions?.manageDiscord;
  };

  return (
    <div className="app-layout">
      {/* Mobile header */}
      <div className="mobile-header">
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
          <Bars3Icon style={{ width: 24, height: 24 }} />
        </button>
        <div className="mobile-logo-wrap">
          <img src="/clubmaster.png" alt="ClubMaster" className="mobile-logo-img" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setShowNotifications(!showNotifications)}>
            <BellIcon style={{ width: 22, height: 22, color: 'var(--text-muted)' }} />
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </div>
          <div className="mobile-avatar" onClick={() => navigate('/profile')}>
            {user?.avatar ? (
              <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            ) : (
              user?.displayName?.charAt(0)?.toUpperCase() || 'U'
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <img src="/clubmaster.png" alt="ClubMaster" className="sidebar-logo-img" />
            <button className="mobile-close-btn" onClick={() => setSidebarOpen(false)}>
              <XMarkIcon style={{ width: 22, height: 22 }} />
            </button>
          </div>
          <div className="user-info" style={{ cursor: 'pointer' }} onClick={() => navigate('/profile')}>
            <div className="user-avatar" style={{ overflow: 'hidden' }}>
              {user?.avatar ? (
                <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                user?.displayName?.charAt(0)?.toUpperCase() || 'U'
              )}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.displayName}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Main</div>
            <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><Squares2X2Icon className="icon" /></span>
              Dashboard
            </NavLink>
          </div>

          {/* Organization-specific navigation */}
          {sidebarOrgs.length > 0 && (
            <div className="nav-section">
              <div className="nav-section-title">Your Organizations</div>
              {sidebarOrgs.map(org => (
                <div key={org._id} className="org-group">
                  <div className="org-toggle" onClick={() => toggleOrg(org._id)}>
                    <div
                      className="org-icon"
                      onClick={(e) => { e.stopPropagation(); navigate(`/org/${org._id}`); setSidebarOpen(false); }}
                      title={org.name}
                    >
                      {org.logo ? <img src={org.logo} alt="" /> : org.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {org.name}
                    </span>
                    <span className={`toggle-arrow ${expandedOrgs[org._id] ? 'open' : ''}`}>
                      <ChevronRightIcon className="icon" />
                    </span>
                  </div>
                  {expandedOrgs[org._id] && (
                    <div className="org-subnav">
                      <NavLink
                        to={`/org/${org._id}`}
                        end
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="nav-icon"><Squares2X2Icon className="icon" /></span>
                        Dashboard
                      </NavLink>
                      <NavLink
                        to={`/org/${org._id}/calendar`}
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="nav-icon"><CalendarIcon className="icon" /></span>
                        Calendar
                      </NavLink>
                      <NavLink
                        to={`/org/${org._id}/events`}
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="nav-icon"><TicketIcon className="icon" /></span>
                        Events
                      </NavLink>
                      <NavLink
                        to={`/org/${org._id}/feedback`}
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="nav-icon"><ChatBubbleLeftEllipsisIcon className="icon" /></span>
                        Feedback
                      </NavLink>
                      {canManageBoards && (
                        <NavLink
                          to={`/org/${org._id}/boards`}
                          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        >
                          <span className="nav-icon"><ClipboardDocumentListIcon className="icon" /></span>
                          Boards
                        </NavLink>
                      )}
                      {user?.role === 'admin' && (
                        <NavLink
                          to={`/org/${org._id}/outfit`}
                          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        >
                          <span className="nav-icon"><SwatchIcon className="icon" /></span>
                          Outfit
                        </NavLink>
                      )}
                      <NavLink
                        to={`/org/${org._id}/documents`}
                        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="nav-icon"><FolderIcon className="icon" /></span>
                        Documents
                      </NavLink>
                      {canManageDiscord(org._id) && (
                        <NavLink
                          to={`/org/${org._id}/discord`}
                          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        >
                          <span className="nav-icon"><ChatBubbleOvalLeftEllipsisIcon className="icon" /></span>
                          Discord
                        </NavLink>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Admin section */}
          {user?.role === 'admin' && (
            <div className="nav-section">
              <div className="nav-section-title">Admin</div>
              <NavLink to="/users" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon"><UserPlusIcon className="icon" /></span>
                User Management
              </NavLink>
              <NavLink to="/logs" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon"><DocumentTextIcon className="icon" /></span>
                Activity Logs
              </NavLink>
              <NavLink to="/activity" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon"><ChartBarIcon className="icon" /></span>
                Activity Stats
              </NavLink>
              <NavLink to="/admin-discord" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon"><CommandLineIcon className="icon" /></span>
                Admin Discord
              </NavLink>
            </div>
          )}

          {user?.role !== 'admin' && user?.permissions?.adminDiscord && (
            <div className="nav-section">
              <div className="nav-section-title">Discord</div>
              <NavLink to="/admin-discord" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon"><CommandLineIcon className="icon" /></span>
                Admin Discord
              </NavLink>
            </div>
          )}

          {/* Owner/Leader section */}
          {(user?.role === 'owner' || user?.role === 'leader') && (
            <div className="nav-section">
              <div className="nav-section-title">Management</div>
              <NavLink to="/members" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon"><UserGroupIcon className="icon" /></span>
                Members
              </NavLink>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-item" onClick={toggleTheme} style={{ cursor: 'pointer' }}>
            <span className="nav-icon">
              {theme === 'dark' ? <SunIcon className="icon" /> : <MoonIcon className="icon" />}
            </span>
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </div>
          <NavLink to="/profile" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <span className="nav-icon"><Cog6ToothIcon className="icon" /></span>
            Account Settings
          </NavLink>
          <div className="nav-item" onClick={handleLogout} style={{ color: 'var(--danger)' }}>
            <span className="nav-icon"><ArrowLeftStartOnRectangleIcon className="icon" /></span>
            Sign Out
          </div>
        </div>
      </aside>

      <main className="main-content">
        {/* Notification bell - desktop */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 6 }}
            >
              <BellIcon style={{ width: 22, height: 22, color: 'var(--text-muted)' }} />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: 0, right: 0, background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <>
                <div className="notif-overlay" onClick={() => setShowNotifications(false)} />
                <div className="notif-dropdown">
                  <div className="notif-dropdown-header">
                    <strong style={{ fontSize: 14 }}>Notifications</strong>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12 }}>Mark all read</button>
                    )}
                  </div>
                  <div className="notif-dropdown-body">
                    {(!notifications || notifications.length === 0) ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No notifications</div>
                    ) : (
                      notifications.slice(0, 5).map(n => (
                        <div
                          key={n._id}
                          onClick={() => {
                            if (!n.read) handleMarkRead(n._id);
                            if (n.eventId && n.organizationId) {
                              setShowNotifications(false);
                              navigate(`/org/${n.organizationId}/events`);
                            }
                          }}
                          className={`notif-item ${n.read ? 'read' : 'unread'}`}
                          style={{ cursor: n.eventId ? 'pointer' : (n.read ? 'default' : 'pointer') }}
                        >
                          <div className="notif-icon">{n.type === 'event_reminder' ? '⏰' : n.type === 'event_updated' ? '✏️' : '📅'}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: n.read ? 400 : 600 }}>{n.title}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{buildNotifMessage(n)}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{new Date(n.createdAt).toLocaleString()}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
